// Single source of truth for every non-Japanese language the translator
// supports. Japanese is always one side of the pair (see TranslatePage.tsx);
// everything below only describes the "other" side, so adding a language is
// "add one entry here," not "touch five different lookup tables by hand."
//
// `name` is the English identifier used to build i18n key names (e.g.
// "Spanish" -> "japaneseToSpanish", "otherLanguageSpanish") - it is never
// rendered directly; the actual on-screen text always comes from
// translatePage.json via i18next, in whichever language the page is in.
export const OTHER_LANGUAGES = [
    { code: "en", name: "English" },
    { code: "pt", name: "Portuguese" },
    { code: "es", name: "Spanish" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "nl", name: "Dutch" },
    { code: "it", name: "Italian" },
    { code: "zh", name: "Chinese" },
    { code: "ko", name: "Korean" },
    { code: "ru", name: "Russian" },
    { code: "ar", name: "Arabic" },
] as const;

export type OtherLanguage = (typeof OTHER_LANGUAGES)[number]["code"];

// A representative flag for each language's dropdown option - not a claim
// that the language belongs only to that one country (same caveat as
// CURRENCY_FLAGS in consts/currencies.ts, e.g. Spanish/Arabic are spoken far
// beyond Spain/Saudi Arabia). Chinese and Arabic don't have one "home"
// country flag the way most of these do; zh uses China's flag since this
// translator's "zh" is specifically simplified Chinese (Google Translate
// v2's code for it), and ar uses Saudi Arabia's as the most commonly used
// representative flag for the language in UI contexts like this one.
const LANGUAGE_FLAGS: Record<OtherLanguage, string> = {
    en: "🇬🇧",
    pt: "🇵🇹",
    es: "🇪🇸",
    fr: "🇫🇷",
    de: "🇩🇪",
    nl: "🇳🇱",
    it: "🇮🇹",
    zh: "🇨🇳",
    ko: "🇰🇷",
    ru: "🇷🇺",
    ar: "🇸🇦",
};

export function getLanguageFlag(code: OtherLanguage): string {
    return LANGUAGE_FLAGS[code] ?? "";
}

// Japanese is always one side; the other side is any OtherLanguage. Widens
// automatically as OTHER_LANGUAGES grows - no hand-maintained string union.
export type Direction = `ja-${OtherLanguage}` | `${OtherLanguage}-ja`;

// Builds the direction for a given "other language" choice while keeping
// which side Japanese is currently on (source vs. target) unchanged.
export function directionFor(jaIsSource: boolean, otherLanguage: OtherLanguage): Direction {
    return jaIsSource ? `ja-${otherLanguage}` : `${otherLanguage}-ja`;
}

// Every Direction is either "ja-<code>" or "<code>-ja" by construction.
export function isJaSource(direction: Direction): boolean {
    return direction.startsWith("ja-");
}

export function otherLanguageOf(direction: Direction): OtherLanguage {
    const code = isJaSource(direction) ? direction.slice(3) : direction.slice(0, -3);
    return code as OtherLanguage;
}

function capitalizeCode(code: OtherLanguage): string {
    return code.charAt(0).toUpperCase() + code.slice(1);
}

function lowerFirst(value: string): string {
    return value.charAt(0).toLowerCase() + value.slice(1);
}

// Points each Direction at its block of translator copy in
// translatePage.json (jaEn/enJa/jaPt/ptJa/jaEs/esJa/...).
export const COPY_NAMESPACE: Record<Direction, string> = OTHER_LANGUAGES.reduce((acc, { code }) => {
    const cap = capitalizeCode(code);
    acc[`ja-${code}`] = `ja${cap}`;
    acc[`${code}-ja`] = `${code}Ja`;
    return acc;
}, {} as Record<Direction, string>);

export interface SwitchMeta {
    labelKey: string;
    arrowKey: string;
    nextDirection: Direction;
}

// Describes the swap button for each Direction: the copy keys for the
// direction swapping leads *to*, and that next Direction itself.
export const SWITCH_META: Record<Direction, SwitchMeta> = OTHER_LANGUAGES.reduce((acc, { code, name }) => {
    const lowerName = lowerFirst(name);
    acc[`ja-${code}`] = { labelKey: `${lowerName}ToJapanese`, arrowKey: `${lowerName}ToJapaneseArrow`, nextDirection: `${code}-ja` };
    acc[`${code}-ja`] = { labelKey: `japaneseTo${name}`, arrowKey: `japaneseTo${name}Arrow`, nextDirection: `ja-${code}` };
    return acc;
}, {} as Record<Direction, SwitchMeta>);

// i18n key for this language's option label in the "other language" picker
// (e.g. "otherLanguageSpanish"), translated per-locale in translatePage.json.
export function otherLanguageLabelKey(code: OtherLanguage): string {
    const entry = OTHER_LANGUAGES.find((language) => language.code === code);
    return `otherLanguage${entry?.name ?? capitalizeCode(code)}`;
}

// MyMemory's free fallback API langpair format ("ja|en"), keyed the same way.
export const MYMEMORY_LANGPAIR: Record<Direction, string> = OTHER_LANGUAGES.reduce((acc, { code }) => {
    acc[`ja-${code}`] = `ja|${code}`;
    acc[`${code}-ja`] = `${code}|ja`;
    return acc;
}, {} as Record<Direction, string>);

// Directions for which the MyMemory fallback is switched off: when Google is
// unavailable the translator then reports "no reliable translation" instead of
// asking MyMemory. Empty on purpose - list a Direction here (e.g. after finding
// MyMemory is consistently wrong for it) rather than hard-coding a language in
// translate.ts.
export const MYMEMORY_FALLBACK_DISABLED: Direction[] = [];
